import { AccountHolderWalkthroughSteps, NewAccountWalkthroughSteps } from '@/types/walkthroughs';
import {
    AnalyticsCTAEvent,
    useTrackCTAClickedAnalytics,
} from '@/utils/amplitude/analytics/cta/trackCTAClicked.analytics';
import { CompleteAccountModalContext, openCompleteAccountSignUpModal, openLoginModal } from '@/redux/modules/modal';
import { en } from '@liveauctioneers/hammer-ui-liveauctioneers/intl/en';
import { EnableSmsOnSavePrompt } from './components/EnableSmsOnSavePrompt/EnableSmsOnSavePrompt';
import { EventContext } from '@/redux/modules/analytics';
import { fetchIncompleteOrdersCountIfNeeded } from '@/redux/modules/wonItems/wonItems.actions';
import {
    getBidderCountryAllowedToReceiveSMS,
    getBidderId,
    isUserLoggedIn,
} from '@/redux/modules/account/user/user.selectors';
import { getIncompleteOrderCount } from '@/redux/modules/wonItems/wonItems.selectors';
import { getItemIdFromPath } from '@/utils/urls';
import { getMyBidItemIds } from '@/redux/modules/bidder/biddingInfoAll/bidderBiddingInfoAll.selectors';
import { getUnreadInboxCount } from '@/redux/modules/conversation/folders/conversationFolders.selectors';
import { Header } from '@liveauctioneers/hammer-ui-liveauctioneers/header';
import { HeaderExperiment } from '@liveauctioneers/hammer-ui-liveauctioneers/headerExperiment';
import {
    HeaderNavigationEventType,
    useTrackCompleteAccountCTAClicked,
    useTrackHeaderCategoryClickedAnalytics,
    useTrackMyAccountIconClicked,
} from '@/utils/amplitude/analytics';
import { logOutBidder } from '@/redux/modules/account/logout/logout.actions';
import { onboardingWalkthroughCurrentStep } from '@/redux/modules/walkthroughs/walkthroughs.selectors';
import {
    setOnboardingWalkthroughOpenProperty,
    setOnboardingWalkthroughStep,
} from '@/redux/modules/walkthroughs/walkthroughs.reducer';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useBrowserAlertsProps } from './hooks/useBrowserAlertsProps';
import { useCallback, useEffect } from 'react';
import { useCategoryNavigationAdsProps } from './hooks/useCategoryNavigationAdsProps';
import { useLocation } from 'react-router-dom';
import { useNotificationDataProps } from './hooks/useNotificationDataProps';
import { useSearchProps } from './hooks/useSearchProps';
import { useSimplifiedHeaderExperiment } from '@/utils/amplitude/experiments/hooks/useSimplifiedHeaderExperiment';
import { useTrackCategoryInNavClicked } from '@/hooks/kameleoon/tracking/useTrackCategoryInNavClicked';
import { useTrackCompleteAccountSetupClicked } from '@/hooks/kameleoon/tracking/useTrackCompleteAccountSetup';
import { useTrackForYouLoggedInButtonClicked, useTrackWonItemsLoggedInButtonClicked } from '@/hooks/kameleoon/tracking';
import { useTrackHeaderNavigationClicked } from '@/utils/amplitude/analytics/navigation';
import { useWhiteLabelProps } from './hooks/useWhiteLabelProps';

const MAXIMUM_WON_ITEMS_DISPLAY_COUNT = 10;
const NAVIGATION_DROPDOWN_Z_INDEX_LAYER = 12;

export const HammerHeader = () => {
    const dispatch = useAppDispatch();
    const userLoggedIn = useAppSelector(isUserLoggedIn);
    const currentStep = useAppSelector(onboardingWalkthroughCurrentStep);
    const unreadCount = useAppSelector(getUnreadInboxCount);
    const incompleteOrdersCount = useAppSelector(getIncompleteOrderCount);
    const { upcomingBidItemIds = [] } = useAppSelector(getMyBidItemIds);
    const upcomingBidsCount = upcomingBidItemIds.length;
    const bidderId = useAppSelector(getBidderId);
    const trackCompleteAccountCTAClicked = useTrackCompleteAccountCTAClicked();
    const trackMyAccountIconClicked = useTrackMyAccountIconClicked();
    const trackCompleteAccountSetupClicked = useTrackCompleteAccountSetupClicked();
    const { pathname } = useLocation();
    const itemId = getItemIdFromPath(pathname);
    const smsAllowed = useAppSelector(getBidderCountryAllowedToReceiveSMS);

    const trackHeaderCategoryClicked = useTrackHeaderCategoryClickedAnalytics();
    const trackCategoryInNavClickedConversion = useTrackCategoryInNavClicked();

    const trackForYouLoggedInButtonClicked = useTrackForYouLoggedInButtonClicked();
    const trackWonItemsLoggedInButtonClicked = useTrackWonItemsLoggedInButtonClicked();

    const { hasHighContrastBackgroundTreatmentA, hasSearchBarWithBorderTreatmentB } = useSimplifiedHeaderExperiment();

    const onLogoutClicked = useCallback(() => dispatch(logOutBidder()), [dispatch]);

    const trackCTAClicked = useTrackCTAClickedAnalytics();
    const trackHeaderNavigationClicked = useTrackHeaderNavigationClicked();

    const handleSetupLink = () => {
        dispatch(openCompleteAccountSignUpModal());
        trackCompleteAccountCTAClicked();
        trackCompleteAccountSetupClicked();
    };

    const onLoginClicked = useCallback(() => {
        trackCTAClicked(AnalyticsCTAEvent.HammerHeader.Login);
        dispatch(
            openLoginModal({
                eventContext: EventContext.join,
                id: itemId,
                itemId,
                type: CompleteAccountModalContext.Login,
            })
        );
    }, [dispatch, itemId, trackCTAClicked]);

    const onSignUpClicked = useCallback(() => {
        trackCTAClicked(AnalyticsCTAEvent.HammerHeader.Join);
        dispatch(
            openCompleteAccountSignUpModal({
                eventContext: EventContext.join,
                id: itemId,
                itemId,
                type: CompleteAccountModalContext.Join,
            })
        );
        if (currentStep === AccountHolderWalkthroughSteps.Returning) {
            dispatch(setOnboardingWalkthroughOpenProperty(false));
            window.setTimeout(() => {
                dispatch(setOnboardingWalkthroughStep(NewAccountWalkthroughSteps.FirstTimeAnyPage));
            }, 400);
        }
    }, [currentStep, dispatch, itemId, trackCTAClicked]);

    const onTrackCategoryClick = useCallback(
        (categoryName: string) => {
            trackHeaderCategoryClicked({ categoryName });
            trackCategoryInNavClickedConversion();
        },
        [trackHeaderCategoryClicked, trackCategoryInNavClickedConversion]
    );

    useEffect(() => {
        if (bidderId > 0) {
            dispatch(fetchIncompleteOrdersCountIfNeeded({ bidderId, force: Boolean(bidderId) }));
        }
    }, [bidderId, dispatch]);

    const props = {
        browserAlerts: useBrowserAlertsProps(),
        callbacks: {
            trackForYouLoggedInButtonClicked,
            trackWonItemsLoggedInButtonClicked,
        },
        categoryNavigationAds: useCategoryNavigationAdsProps(),
        experiments: {
            isCompleteProfileCTAEnabled: false,
            showWonItemsLoggedInButton: false,
        },
        isUserLoggedIn: userLoggedIn,
        login: onLoginClicked,
        logout: onLogoutClicked,
        notificationData: useNotificationDataProps(),
        search: useSearchProps(),
        signUp: onSignUpClicked,
        trackCategoryClick: onTrackCategoryClick,
        unreadCount,
        upcomingBidsCount,
        wonItemsCount: incompleteOrdersCount,
        wonItemsMaximumCount: MAXIMUM_WON_ITEMS_DISPLAY_COUNT,
        ...useWhiteLabelProps(),
        intl: en,
        zIndexes: {
            megaNavDropdown: NAVIGATION_DROPDOWN_Z_INDEX_LAYER,
            searchDropdown: NAVIGATION_DROPDOWN_Z_INDEX_LAYER,
        },
    };

    const headerComponent =
        hasHighContrastBackgroundTreatmentA || hasSearchBarWithBorderTreatmentB ? (
            <HeaderExperiment
                {...props}
                analytics={{
                    onAlertsClicked: () => {
                        trackHeaderNavigationClicked({ eventType: HeaderNavigationEventType.Alerts });
                    },
                    onMyItemsClicked: () => {
                        trackHeaderNavigationClicked({ eventType: HeaderNavigationEventType.MyItems });
                    },
                    onWonItemsClicked: () => {
                        trackHeaderNavigationClicked({ eventType: HeaderNavigationEventType.WonItems });
                    },
                }}
                experiments={{
                    ...props.experiments,
                    isHighContrastHeader: hasHighContrastBackgroundTreatmentA,
                    isProminentSearchInput: hasSearchBarWithBorderTreatmentB,
                }}
                onLoginClicked={onLoginClicked}
                onLogoutClicked={onLogoutClicked}
                onSignUpClicked={onSignUpClicked}
            />
        ) : (
            <Header
                {...props}
                onCompleteProfileCTAClicked={handleSetupLink}
                onMyProfileClicked={trackMyAccountIconClicked}
            />
        );

    return (
        <>
            {headerComponent}
            {smsAllowed && <EnableSmsOnSavePrompt />}
        </>
    );
};
